/**
 * Combat-result badges for the party panel.
 *
 * Combat resolves a lot per round and then most of it only exists in narration,
 * which scrolls away exactly when it mattered. These pills put the results the
 * player needs back where they look: the party roster.
 *
 * Lifetime, and why they differ:
 *   - Heroic Inspiration persists until spent (it's a held resource).
 *   - Death saves persist while the character is dying.
 *   - Last save + cover are combat-scoped and cleared on combat exit, so they
 *     can't turn into the sticky-marker bug (a "failed DEX save" pill following
 *     someone into a quiet tavern).
 *   - Crit markers are per-round and cleared at round start.
 *
 * Each returns `null` when there's nothing to say, so callers can render them
 * unconditionally.
 */

import type { CoverType, LastSavingThrow, SaveAbilityShort } from '@danda/shared';
import { StatusPill, type StatusPillSize } from '@/components/ui/status-pill';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

interface BadgeBaseProps {
  size?: StatusPillSize;
  className?: string;
}

/** Wrap a pill in the hover-explanation shape the other status badges use. */
function ExplainedPill({
  label,
  explanation,
  pillClassName,
  size = 'xs',
  className,
  ariaLabel,
}: {
  label: string;
  explanation: string;
  pillClassName: string;
  size?: StatusPillSize;
  className?: string;
  ariaLabel: string;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <StatusPill
            size={size}
            aria-label={ariaLabel}
            className={`${pillClassName} cursor-help ${className ?? ''}`}
          >
            {label}
          </StatusPill>
        </TooltipTrigger>
        <TooltipContent>
          <div>{explanation}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Heroic Inspiration (SRD 2024): expend it to reroll any die, and you must use
 * the new roll. Fully implemented in the engine and awarded by the GM; until now
 * the web client never showed the player they were holding it.
 */
export function HeroicInspirationBadge({
  hasInspiration,
  size = 'xs',
  className,
}: BadgeBaseProps & { hasInspiration?: boolean }) {
  if (!hasInspiration) return null;
  return (
    <ExplainedPill
      label="✨ INSPIRED"
      ariaLabel="Has Heroic Inspiration"
      explanation="Heroic Inspiration: spent automatically to reroll a failed D20 Test. You can only hold one."
      pillClassName="bg-yellow-500 text-yellow-950"
      size={size}
      className={className}
    />
  );
}

const SAVE_ABILITY_LABEL: Record<SaveAbilityShort, string> = {
  str: 'STR',
  dex: 'DEX',
  con: 'CON',
  int: 'INT',
  wis: 'WIS',
  cha: 'CHA',
};

/**
 * The most recent saving throw. The margin is the point of this badge: "failed
 * by 1" is a different story from "failed by 12", and it's the detail narration
 * loses first.
 */
export function LastSavingThrowBadge({
  save,
  size = 'xs',
  className,
}: BadgeBaseProps & { save?: LastSavingThrow }) {
  if (!save) return null;

  const ability = SAVE_ABILITY_LABEL[save.ability] ?? save.ability.toUpperCase();
  const margin = save.total - save.dc;
  const label = `${ability} ${save.succeeded ? 'SAVE' : 'FAIL'}`;

  // A pass or fail by one point is the clutch moment; call it out by name.
  const closeness =
    Math.abs(margin) <= 1
      ? save.succeeded
        ? ': held by a single point'
        : ': missed by a single point'
      : '';
  const against = save.source ? ` against ${save.source}` : '';
  const explanation = `${ability} save${against}: rolled ${save.total} vs DC ${save.dc}${closeness}`;

  return (
    <ExplainedPill
      label={label}
      ariaLabel={`Last saving throw: ${ability} ${save.succeeded ? 'succeeded' : 'failed'}`}
      explanation={explanation}
      pillClassName={save.succeeded ? 'bg-emerald-700 text-emerald-50' : 'bg-rose-700 text-rose-50'}
      size={size}
      className={className}
    />
  );
}

/**
 * Death save tally for a downed character. Three successes stabilize, three
 * failures kill: the one running total a player must not have to scroll for.
 */
export function DeathSaveTallyBadge({
  successes,
  failures,
  size = 'xs',
  className,
}: BadgeBaseProps & { successes?: number; failures?: number }) {
  const s = successes ?? 0;
  const f = failures ?? 0;
  // Nothing to show before the first save, and nothing useful to show once the
  // tally has resolved (3 failures is DEAD, which the status pill already says).
  if (s === 0 && f === 0) return null;
  if (f >= 3) return null;

  return (
    <ExplainedPill
      label={`DEATH SAVES ${s}✓ ${f}✗`}
      ariaLabel={`Death saves: ${s} successes, ${f} failures`}
      explanation={`${s} of 3 successes, ${f} of 3 failures. Three successes stabilize; three failures are fatal.`}
      pillClassName={f >= 2 ? 'bg-red-800 text-red-50' : 'bg-slate-700 text-slate-100'}
      size={size}
      className={className}
    />
  );
}

/**
 * Crit dealt or taken this round. Purely transient: it means something only for
 * the round it happened in, so the backend clears it at round start.
 */
export function CritMarkerBadge({
  dealt,
  taken,
  size = 'xs',
  className,
}: BadgeBaseProps & { dealt?: boolean; taken?: boolean }) {
  if (!dealt && !taken) return null;

  // Both can be true in the same round: one line, both facts.
  const label = dealt && taken ? '💥 CRIT ⚡ CRIT TAKEN' : dealt ? '💥 CRIT' : '⚡ CRIT TAKEN';
  const explanation =
    dealt && taken
      ? 'Landed a critical hit and took one this round.'
      : dealt
        ? 'Landed a critical hit this round (damage dice rolled twice).'
        : 'Took a critical hit this round.';

  return (
    <ExplainedPill
      label={label}
      ariaLabel={explanation}
      explanation={explanation}
      pillClassName={dealt ? 'bg-amber-600 text-amber-50' : 'bg-orange-800 text-orange-50'}
      size={size}
      className={className}
    />
  );
}

const COVER_LABEL: Partial<Record<CoverType, { label: string; explanation: string }>> = {
  half: {
    label: '🛡 HALF COVER',
    explanation: 'Half cover: +2 to AC and Dexterity saving throws.',
  },
  three_quarters: {
    label: '🛡 3/4 COVER',
    explanation: 'Three-quarters cover: +5 to AC and Dexterity saving throws.',
  },
  total: {
    label: '🛡 TOTAL COVER',
    explanation: "Total cover: can't be targeted directly by attacks or spells.",
  },
};

/**
 * Active cover degree. Only the most protective degree ever applies (SRD 5.2 ,
 * cover is never summed), which is why this is one badge and not a stack.
 *
 * Expect this to stay empty until scenes carry authored grid cover: today the
 * only source is feature-granted cover (Smite of Protection, Nature's Sanctuary).
 */
export function CoverBadge({
  cover,
  size = 'xs',
  className,
}: BadgeBaseProps & { cover?: CoverType }) {
  if (!cover || cover === 'none') return null;
  const entry = COVER_LABEL[cover];
  if (!entry) return null;

  return (
    <ExplainedPill
      label={entry.label}
      ariaLabel={entry.explanation}
      explanation={entry.explanation}
      pillClassName="bg-sky-800 text-sky-50"
      size={size}
      className={className}
    />
  );
}
